import { compact, get, isEmpty, isNil, uniq } from 'lodash'
import fp from 'lodash/fp'
import { toJS } from 'mobx'
import { z } from 'zod'
import { isAdultBySsn } from 'common/src/vtj/ssn'
import { validateHetu } from 'common/src/vtj/validation/validators-typed'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { isCountryKoodistoCode } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { REGEX_LAST_NAME_NOT_ALLOWED_CHARACTERS } from 'todistukset-common/src/vtj/form-sanitization.util'
import {
  ErrorTranslationParams,
  isValidPhoneNumber,
  removeSpaces,
  ValidationError,
  ValueValidator,
} from 'todistukset-common/src/vtj/form-validation.util'
import {
  EditableField,
  finalEditableFieldValue,
  hasEditableFieldValue,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  availableAETTodistusLanguages,
  availableUAETTodistusLanguages,
} from 'todistukset-common/src/vtj/language.util'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import { PlaceOfMarriage } from 'todistukset-common/src/vtj/types/place-of-marriage'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { isHetuIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import {
  TodistusStoreForm,
  AetOrdererUiForm,
  AetSignerUiForm,
  UaetUiForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import type { UaetFormPropertyName } from 'todistukset-common/src/vtj/uaet-validation'

type AetForm = AetOrdererUiForm | AetSignerUiForm
type AetFormPropertyName =
  | keyof AetOrdererUiForm
  | keyof AetSignerUiForm
  | 'contactInfo.phoneNumber.ilmoitettuArvo'
  | 'contactInfo.email.ilmoitettuArvo'
  | 'orderer.countryOfResidenceCode.ilmoitettuArvo'
  | 'orderer.numberOfMarriages'
  | 'orderer.maritalStatus'
  | 'newLastName.ilmoitettuArvo'
  | 'spouseNewLastName.ilmoitettuArvo'
  | 'keepingCurrentLastName.ilmoitettuArvo'
  | 'spouseKeepingCurrentLastName.ilmoitettuArvo'
  | 'placeOfMarriage.ilmoitettuArvo'
  | 'todistusLanguage.ilmoitettuArvo'
  | 'sukulaisuussuhdeVakuutettu.ilmoitettuArvo'
  | 'orderer.maritalStatus.ilmoitettuArvo'
type Validator = (validations: Validation[]) => ValidationError[]
type AetFormValueValidator = ValueValidator<
  Partial<{ [Key in AetFormPropertyName]: unknown }>
>
type AetEditableFieldFormValueValidator = (
  editableField: EditableField<unknown> | null,
  form: Partial<{ [Key in AetFormPropertyName]: unknown }>
) => undefined | ValidationError
type AetFormValueValidatorNoUser = (
  value: Parameters<AetFormValueValidator>[0],
  form: Parameters<AetFormValueValidator>[1]
) => ReturnType<AetFormValueValidator>
type AetFormValueValidatorNoUserNoForm = (
  value: Parameters<AetFormValueValidator>[0]
) => ReturnType<AetFormValueValidator>
type Validation = [
  AetFormValueValidator | AetEditableFieldFormValueValidator,
  AetFormPropertyName | UaetFormPropertyName
]

export const MAX_NUMBER_OF_MARRIAGES = 20
export const MAX_LENGTH_OF_LAST_NAME = 100
export const MAX_KANSALAISUUSKOODI_ARVO_COUNT = 20
export const MAX_LENGTH_OF_MAIL_DELIVERY_FIELD = 100

const PHONE_MIN_SIGNIFICANT_CHARACTERS = 4
const PHONE_MAX_SIGNIFICANT_CHARACTERS = 20
const EMAIL_MIN_POSSIBLE_VALID_LENGTH = 5 // a@q.c
const EMAIL_DB_FIELD_MAX_LENGTH = 100 // A valid email could be longer, but our db field is the limiting factor

export interface ValidateParams {
  form: AetForm | TodistusStoreForm | UaetUiForm
  user: TodistuksetUser
}

export const isPlaceOfMarriageUnsupported = (p: unknown): boolean => {
  return p === PlaceOfMarriage.OTHER
}

export const validate =
  ({ form, user }: ValidateParams): Validator =>
  (validations: Validation[]): ValidationError[] => {
    return compact(
      validations.map(([validationFn, propertyName]) => {
        return validationFn(toJS(get(form, propertyName)), form, user)
      })
    )
  }

const createValidationError =
  (propertyName: AetFormPropertyName) =>
  (
    errorTranslationKey: string,
    errorTranslationParams?: ErrorTranslationParams
  ): ValidationError => {
    return {
      propertyName,
      errorTranslationKey,
      ...(errorTranslationParams ? { errorTranslationParams } : {}),
    }
  }

export const validateOrdererInfoFields = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [
      validateCountryOfResidenceCode,
      'orderer.countryOfResidenceCode.ilmoitettuArvo',
    ],
    [validateMaritalStatus, 'orderer.maritalStatus.ilmoitettuArvo'],
    [validateNumberOfMarriages, 'orderer.numberOfMarriages'],
  ])
}

export const validateOrdererSpouseInfoFields = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateSpouseNotRelated, 'sukulaisuussuhdeVakuutettu.ilmoitettuArvo'],
    [validateSpouseHetu, 'spouseHetu'],
    [validateSpouseEmail, 'spouseEmail'],
  ])
}

export const validateSignerSpouseInfoFields = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateSpouseNotRelated, 'sukulaisuussuhdeVakuutettu.ilmoitettuArvo'],
  ])
}

export const validateLastnameChoiceFields = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateKeepingCurrentLastName, 'keepingCurrentLastName.ilmoitettuArvo'],
    [
      validateSpouseKeepingCurrentLastName,
      'spouseKeepingCurrentLastName.ilmoitettuArvo',
    ],
    [validateNewLastName, 'newLastName.ilmoitettuArvo'],
    [validateSpouseNewLastName, 'spouseNewLastName.ilmoitettuArvo'],
  ])
}

export const validateContactInfoFields = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateContactPhoneNumber, 'contactInfo.phoneNumber.ilmoitettuArvo'],
    [validateContactEmail, 'contactInfo.email.ilmoitettuArvo'],
  ])
}

export const validateAdditionalSelectionSteps = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validatePlaceOfMarriage, 'placeOfMarriage.ilmoitettuArvo'],
    [validateAetTodistusLanguage, 'todistusLanguage.ilmoitettuArvo'],
  ])
}

export const validateCountryOfResidenceCode: AetFormValueValidator = (
  value,
  _form,
  user
) => {
  const validationError = createValidationError(
    'orderer.countryOfResidenceCode.ilmoitettuArvo'
  )

  const userHasCountryOfResidence =
    isHetuIdentifiedUser(user) &&
    hasEditableFieldValue(user.countryOfResidenceCode)

  if (isEmpty(value) && userHasCountryOfResidence) {
    return undefined
  }

  if (isEmpty(value)) {
    return validationError('validationRequiredValueMissing')
  }

  if (isCountryKoodistoCode(value)) {
    return undefined
  }

  return validationError('validationInvalidCountryOfResidence')
}

export const validateNumberOfMarriages: AetEditableFieldFormValueValidator = (
  editableField,
  form
) => {
  const validationError = createValidationError('orderer.numberOfMarriages')
  const value = finalEditableFieldValue(editableField)

  if (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore The form values of validators are not typed, hence we ignore typechecking here.
    finalEditableFieldValue(fp.get('orderer.maritalStatus', form) ?? null) ===
      MaritalStatus.NAIMATON ||
    !fp.isNil(editableField?.vtjArvo)
  ) {
    return undefined
  }

  if (isNil(value) || !fp.isNumber(value)) {
    return validationError('validationRequiredValueMissing')
  }

  if (value > MAX_NUMBER_OF_MARRIAGES || value < 0) {
    return validationError('validationNumberOfMarriagesExceedsMaximum', {
      maxCount: MAX_NUMBER_OF_MARRIAGES,
    })
  }

  return undefined
}

export const validateMaritalStatus: AetFormValueValidator = (
  value,
  _form,
  user
) => {
  const validationError = createValidationError('orderer.maritalStatus')
  const expectedMaritalStatuses = [
    MaritalStatus.NAIMATON,
    MaritalStatus.LESKI,
    MaritalStatus.ERONNUT,
  ]

  const userHasMaritalStatus =
    isHetuIdentifiedUser(user) &&
    !isNil(finalEditableFieldValue(user.maritalStatus))

  if (userHasMaritalStatus) {
    return undefined
  }

  if (isEmpty(value)) {
    return validationError('validationRequiredValueMissing')
  }

  if (
    isValueInEnum(value, MaritalStatus) &&
    expectedMaritalStatuses.includes(value)
  ) {
    return undefined
  }

  return validationError('validationInvalidMaritalStatus')
}

export const validateKeepingCurrentLastName: AetFormValueValidatorNoUserNoForm =
  (value) =>
    typeof value === 'boolean'
      ? undefined
      : createValidationError('keepingCurrentLastName')(
          'validationMissingOwnLastNameChoice'
        )

export const validateSpouseKeepingCurrentLastName: AetFormValueValidatorNoUserNoForm =
  (value) =>
    typeof value === 'boolean'
      ? undefined
      : createValidationError('spouseKeepingCurrentLastName')(
          'validationMissingSpouseLastNameChoice'
        )

const createLastNameValidator: (
  propertyName: 'newLastName' | 'spouseNewLastName',
  keepCurrentNameField:
    | 'keepingCurrentLastName'
    | 'spouseKeepingCurrentLastName'
) => AetFormValueValidatorNoUser =
  (propertyName, keepCurrentNameField) => (value, form) => {
    const validationError = createValidationError(propertyName)
    // This validation system does not keep track of types of the values, hence:
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    if (finalEditableFieldValue(form[keepCurrentNameField] as any) !== false) {
      return undefined
    }
    if (typeof value === 'string' && value.length > 0) {
      if (value.length > MAX_LENGTH_OF_LAST_NAME) {
        return validationError('validationTooLongLastName')
      }

      const invalidCharacters = uniq(
        value.match(REGEX_LAST_NAME_NOT_ALLOWED_CHARACTERS)
      )
      if (invalidCharacters.length > 0) {
        return validationError('validationInvalidCharacters', {
          characters: invalidCharacters.join(', '),
        })
      }

      return undefined
    }

    return validationError('validationMissingLastName')
  }

export const validateNewLastName = createLastNameValidator(
  'newLastName',
  'keepingCurrentLastName'
)
export const validateSpouseNewLastName = createLastNameValidator(
  'spouseNewLastName',
  'spouseKeepingCurrentLastName'
)

export const validateContactPhoneNumber: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  const validationError = createValidationError(
    'contactInfo.phoneNumber.ilmoitettuArvo'
  )
  const sanitizedValue = typeof value === 'string' ? removeSpaces(value) : ''
  if (sanitizedValue.length < PHONE_MIN_SIGNIFICANT_CHARACTERS) {
    // Comparison to short non-zero value to prefer the 'missing' error over 'invalid' while
    // typing into the field (isValidPhoneNumber reports 'invalid' for under 3-4 chars)
    return validationError('validationRequiredValueMissing')
  }

  if (sanitizedValue.length > PHONE_MAX_SIGNIFICANT_CHARACTERS) {
    return validationError('validationLengthExceedsMaximum', {
      count: PHONE_MAX_SIGNIFICANT_CHARACTERS.toString(),
    })
  }

  if (isValidPhoneNumber(sanitizedValue)) {
    return undefined
  }

  return validationError('validationContactInfoInvalidPhoneNumber')
}

export const validateContactEmail: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  const validationError = createValidationError(
    'contactInfo.email.ilmoitettuArvo'
  )
  if (
    typeof value !== 'string' ||
    value.length < EMAIL_MIN_POSSIBLE_VALID_LENGTH
  ) {
    return validationError('validationRequiredValueMissing')
  }

  if (value.length > 100) {
    return validationError('validationLengthExceedsMaximum', {
      count: EMAIL_DB_FIELD_MAX_LENGTH.toString(),
    })
  }

  const { success } = z.string().email().safeParse(value)
  if (success) {
    return undefined
  }

  return validationError('validationContactInfoInvalidEmail')
}

export const validateSpouseNotRelated: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  if (value !== true) {
    return createValidationError('sukulaisuussuhdeVakuutettu')(
      'validationSpouseNotRelated'
    )
  }
}

export const validateSpouseHetu: AetFormValueValidator = (
  value,
  _form,
  user
) => {
  const validationError = createValidationError('spouseHetu')

  if (isEmpty(value)) {
    return validationError('validationRequiredValueMissing')
  }

  if (!validateHetu(value) || typeof value !== 'string') {
    return validationError('validationInvalidHetu')
  }
  if (!isAdultBySsn(value)) {
    return validationError('validationSpouseHetuUnderage')
  }
  if (isHetuIdentifiedUser(user) && user.hetu === value) {
    return validationError('validationYouCantMarryYourself')
  }
}

export const validateSpouseEmail: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  const validationError = createValidationError('spouseEmail')

  if (isEmpty(value)) {
    return validationError('validationRequiredValueMissing')
  }
  const { success } = z.string().email().safeParse(value)
  if (!success) {
    return validationError('validationContactInfoInvalidEmail')
  }
}

export const validatePlaceOfMarriage: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  if (isEmpty(value)) {
    return createValidationError('placeOfMarriage')(
      'validationMissingPlaceOfWedding'
    )
  }

  if (isPlaceOfMarriageUnsupported(value)) {
    return createValidationError('placeOfMarriage')(
      'aetOrdererAdditionalSelectionsWrongCertificateTitle'
    )
  }
}

export const validateAetTodistusLanguage: AetFormValueValidatorNoUserNoForm = (
  value
) => {
  return validateTodistusLanguage(value, availableAETTodistusLanguages)
}

export const validateTodistusLanguage = (
  languageValue: unknown,
  availableLanguages:
    | typeof availableAETTodistusLanguages
    | typeof availableUAETTodistusLanguages
): ValidationError | undefined => {
  const validationError = createValidationError('todistusLanguage')

  if (isEmpty(languageValue)) {
    return validationError('validationMissingTodistusLanguage')
  }
  if (!fp.includes(languageValue, availableLanguages)) {
    return validationError('validationUnknownError')
  }
}

export const propertyNameTranslationKeysAetAndUaetForms: Partial<{
  [key in AetFormPropertyName | UaetFormPropertyName]: string
}> = {
  'orderer.countryOfResidenceCode.ilmoitettuArvo':
    'aetOwnInfoCountryOfResidenceLabel',
  'contactInfo.email.ilmoitettuArvo': 'aetContactInfoEmailFieldLabel',
  'contactInfo.phoneNumber.ilmoitettuArvo':
    'aetContactInfoPhoneNumberFieldLabel',
  spouseHetu: 'aetSpouseInfoHetuLabel',
  spouseEmail: 'aetSpouseInfoEmailLabel',
  'orderer.numberOfMarriages': 'aetOwnInfoNumberOfMarriagesLabel',
  'orderer.maritalStatus': 'aetOwnInfoMaritalStatusLabel',
  keepingCurrentLastName: 'validationMissingOwnLastNameChoice',
  spouseKeepingCurrentLastName: 'validationMissingSpouseLastNameChoice',
  newLastName: 'aetOrdererLastnameChoiceOwnNewNameLabel',
  spouseNewLastName: 'aetOrdererLastnameChoiceSpouseNewNameLabel',
  sukulaisuussuhdeVakuutettu: 'aetSpouseNotRelatedCheckboxLabel',
  placeOfMarriage: 'aetOrdererAdditionalSelectionsPlaceOfWeddingLabel',
  todistusLanguage: 'aetOrdererAdditionalSelectionsTodistusLanguageLabel',
  'mailDelivery.firstName':
    'orderWizardUaetContactInfoStepInputLabel_firstName',
  'mailDelivery.lastName': 'orderWizardUaetContactInfoStepInputLabel_lastName',
  'spouseA.contactInfo.phoneNumber.ilmoitettuArvo':
    'aetContactInfoPhoneNumberFieldLabel',
  'spouseA.contactInfo.email.ilmoitettuArvo': 'aetContactInfoEmailFieldLabel',
  'spouseA.sukulaisuussuhdeVakuutettu.ilmoitettuArvo':
    'aetSpouseNotRelatedCheckboxLabel',
  'spouseB.firstNames.ilmoitettuArvo': 'uaetSpouseInfoInputLabel_firstNames',
  'spouseB.lastName.ilmoitettuArvo': 'uaetSpouseInfoInputLabel_lastName',
  'spouseB.birthdate.ilmoitettuArvo': 'uaetSpouseInfoInputLabel_birthDate',
  'spouseB.citizenshipCode.ilmoitettuArvo':
    'uaetSpouseInfoInputLabel_citizenships',
  'spouseB.birthPlace.ilmoitettuArvo': 'uaetSpouseInfoInputLabel_birthplace',
  'spouseB.birthCountryCode.ilmoitettuArvo':
    'uaetSpouseInfoInputLabel_birthCountry',
  'spouseB.maritalStatus.ilmoitettuArvo': 'aetOwnInfoMaritalStatusLabel',
}
